import { useEffect, useCallback, useRef } from "react"
import { codeForKey, isApplePlatform } from "./platform"

export interface ShortcutConfig {
  key: string
  ctrl?: boolean
  alt?: boolean
  shift?: boolean
  meta?: boolean
  description: string
  /** Fired on key DOWN. */
  action: () => void
  /**
   * Fired on key UP, for hold-to-act shortcuts like push-to-talk.
   *
   * Also fired if the window loses focus while the key is held — otherwise
   * alt-tabbing mid-sentence would leave the mic live with nothing to turn it
   * off, which is the worst possible failure for this particular feature.
   */
  onRelease?: () => void
  /**
   * Allow the OS auto-repeat to re-fire `action` while held. Off by default:
   * a held key repeats ~30x/second and every toggle shortcut would thrash.
   */
  repeatable?: boolean
  preventDefault?: boolean
}

export function useKeyboardShortcuts(shortcuts: ShortcutConfig[]) {
  const shortcutsRef = useRef(shortcuts)
  /** Shortcuts currently held down, so key-up can find its own release. */
  const heldRef = useRef<ShortcutConfig[]>([])
  
  // Update ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts
  }, [shortcuts])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const { key, ctrlKey, altKey, shiftKey, metaKey } = event
    
    // Don't trigger shortcuts when typing in input fields
    const target = event.target as HTMLElement
    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.contentEditable === "true"
    ) {
      return
    }

    // ⌘ on Apple platforms, Ctrl everywhere else. A config saying `ctrl: true`
    // means "the platform's primary modifier", not the literal Ctrl key — a Mac
    // user pressing ⌘+D expects mute and would never press literal Ctrl+D.
    const applePlatform = isApplePlatform()
    const primaryDown = applePlatform ? metaKey : ctrlKey
    // The other one must be up, or ⌘+Ctrl+D would fire the plain ⌘+D binding.
    const otherPrimaryDown = applePlatform ? ctrlKey : metaKey

    // Find (⌘F / Ctrl+F). Fullscreen deliberately also requires Alt, so plain
    // Find reaches the browser untouched.
    if (key.toLowerCase() === "f" && primaryDown && !altKey) {
      return
    }

    // Find matching shortcut
    const shortcut = shortcutsRef.current.find((s) => {
      // Physical key first. On macOS, Option rewrites the character — ⌥S
      // reports `key === "ß"`, ⌥C reports "ç" — so every Alt shortcut would
      // miss if we compared characters. `code` is also layout-independent.
      const code = codeForKey(s.key)
      const keyMatch =
        (code !== null && code === event.code) ||
        s.key.toLowerCase() === key.toLowerCase()

      const primaryMatch = !!s.ctrl === primaryDown && !otherPrimaryDown
      const altMatch = !!s.alt === altKey
      const shiftMatch = !!s.shift === shiftKey

      return keyMatch && primaryMatch && altMatch && shiftMatch
    })

    if (shortcut) {
      // Auto-repeat would re-fire ~30x/second while a key is held. Push-to-talk
      // must engage once and stay engaged, and toggles would flicker.
      if (event.repeat && !shortcut.repeatable) {
        if (shortcut.preventDefault !== false) event.preventDefault()
        return
      }
      if (shortcut.preventDefault !== false) {
        event.preventDefault()
      }
      // Tracked before the action runs, so a release always finds it even if
      // the action throws.
      if (shortcut.onRelease && !heldRef.current.includes(shortcut)) {
        heldRef.current.push(shortcut)
      }
      shortcut.action()
    }
  }, [])

  // Key UP only matters for hold-to-act shortcuts. Matched on the key alone:
  // modifiers are often released first (or in a different order), and a
  // push-to-talk that only ends when the modifiers happen to still be down
  // would leave the mic open.
  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (heldRef.current.length === 0) return
    const code = event.code
    const key = event.key.toLowerCase()

    const stillHeld: ShortcutConfig[] = []
    for (const held of heldRef.current) {
      const heldCode = codeForKey(held.key)
      const matches = (heldCode !== null && heldCode === code) || held.key.toLowerCase() === key
      if (matches) held.onRelease?.()
      else stillHeld.push(held)
    }
    heldRef.current = stillHeld
  }, [])

  /** Release everything held — the window lost focus mid-hold. */
  const releaseAll = useCallback(() => {
    if (heldRef.current.length === 0) return
    const held = heldRef.current
    heldRef.current = []
    for (const s of held) s.onRelease?.()
  }, [])

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    // Alt-tabbing while holding push-to-talk never delivers the key-up, so the
    // mic would stay live in another window. Blur is the backstop.
    window.addEventListener("blur", releaseAll)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
      window.removeEventListener("blur", releaseAll)
      // Unmounting mid-hold must not strand the held state either.
      releaseAll()
    }
  }, [handleKeyDown, handleKeyUp, releaseAll])
}

// Common shortcuts configuration.
//
// Screen share, chat, participants and captions sit on Alt rather than Ctrl.
// Their Ctrl forms are Save Page, Copy, Print and View Source — all
// interceptable, which is exactly the problem: the meeting would silently
// swallow four things the browser is expected to do, and Copy in particular
// needed a special case in the matcher to stay usable at all.
//
// Mic (Ctrl+D) and camera (Ctrl+E) stay on Ctrl: bookmark-page and search-bar
// are the least missed of the set, and these two are the shortcuts people
// actually use mid-call.
export const COMMON_SHORTCUTS = {
  mute: { key: "d", ctrl: true, description: "Mute/Unmute microphone" },
  muteAll: { key: "d", ctrl: true, shift: true, description: "Mute all participants" },
  video: { key: "e", ctrl: true, description: "Turn camera on/off" },
  // Alt, not Ctrl: Ctrl+S is Save Page. See the note above the map.
  screen: { key: "s", alt: true, description: "Share screen" },
  hand: { key: "h", ctrl: true, description: "Raise/Lower hand" },
  chat: { key: "c", alt: true, description: "Open/Close chat" },
  participants: { key: "p", alt: true, description: "Show/Hide participants" },
  captions: { key: "u", alt: true, description: "Toggle captions" },
  fullscreen: { key: "f", ctrl: true, alt: true, description: "Toggle fullscreen" },
  // `shift` is required, not incidental: typing `?` means holding Shift on every
  // common layout, and the matcher compares `!!s.shift === event.shiftKey`. With
  // it unset the comparison was `false === true` and this shortcut could never
  // fire — it had been dead since it was written.
  help: { key: "?", shift: true, description: "Show keyboard shortcuts" },
  // Alt+L, not Ctrl+W (the browser closes the tab before the page sees it) and
  // not Ctrl/⌘+L (focuses the address bar in every browser). Alt+L is bound by
  // nothing — no browser accelerator, no Firefox menu key — so two keys are
  // enough and a third would only make it harder to press.
  leave: { key: "l", alt: true, description: "Leave meeting" },
  // Space, held. No modifier: the whole point is that it is reachable without
  // thinking. `preventDefault` on key-down is what stops it scrolling the page
  // and activating whatever button happens to have focus.
  pushToTalk: { key: " ", description: "Hold to talk (Push to talk)" },
} as const

export type ShortcutKey = keyof typeof COMMON_SHORTCUTS
